// packages/web/src/screens/Account.tsx — a minimal, generic home for
// account-level actions (Core hardening 2026-07-29): change password
// (POST /auth/password/change) and sign out. Reached via a persistent shell
// icon, not tied to any of this fork's tabs — see routes/route.ts's
// 'account' case for why it always returns to Today rather than tracking a
// return tab. Deliberately a real screen rather than a bare icon action: a
// second account-level action (password change) already exists on day one,
// and a screen is the natural place for future ones (e.g. a session/device
// list) without needing to redesign a bare-icon affordance later.
import { useEffect, useState } from 'preact/hooks';
import { startRegistration } from '@simplewebauthn/browser';
import {
  ApiError,
  changePassword,
  getEntryConfig,
  listTenants,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  whoami,
  type ApiUser,
  type TenantMembership,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { SignOutButton } from '../components/SignOutButton';
import './Account.css';

export interface AccountProps {
  user: ApiUser;
  /** The workspace this shell is currently in — the switcher marks it. */
  activeTenantId: string | null;
  /** B17d: chosen another workspace. App stores id + vertical and lands on
   *  Today; this screen never writes the storage itself (one writer). */
  onSwitchTenant: (tenant: TenantMembership) => void;
  onBack: () => void;
}

function displayName(user: ApiUser): string {
  const trimmed = user.name?.trim();
  if (trimmed) return trimmed;
  const [local] = user.email.split('@');
  return local || user.email;
}

/**
 * ⚑ DOES THIS DEPLOYMENT SIGN ANYBODY IN WITH A PASSWORD?
 *
 * `null` while unread, then the pack's own answer. It exists because the
 * question could not be asked of the USER: `/auth/whoami` answers
 * { id, email, name } and nothing on that payload says whether a password
 * hash exists, so the honest fact available on the client is the DOOR's, not
 * the account's.
 *
 * ⚠ AND THE DOOR'S ANSWER IS "NO" EVERYWHERE TODAY. C6a replaced the
 * four-method wall with one field and then the best door for that person —
 * a magic link, or a passkey. Nothing in this web mints a password any more
 * and there is no forgot-password screen, so an account created since then
 * HAS none, and `POST /auth/password/change` can only answer it "Current
 * password is incorrect". A form that cannot succeed is not a door.
 *
 * The durable fix is one boolean on the whoami payload
 * (`users.password_hash IS NOT NULL`), which belongs in auth.ts; until then
 * this is the fact the client can actually read.
 */
export function usePasswordDoor(): boolean | null {
  const [open, setOpen] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    // Two facts open this door, either alone: the pack signs people in by
    // password, OR this account actually HOLDS one (legacy Auth-2 accounts
    // minted passwords a pack-level flag cannot see, and hiding their only
    // change-password control would lock a real credential in place).
    Promise.all([getEntryConfig(), whoami()])
      .then(([entry, user]) => {
        if (!cancelled) setOpen(entry?.passwordSignIn === true || user?.hasPassword === true);
      })
      .catch(() => {
        // Unknown resolves to ABSENT, not to a form: offering one we cannot
        // stand behind is the failure this hook exists to stop.
        if (!cancelled) setOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return open;
}

/** Exported so the preferences screen can host the SAME form rather than
 * grow a second one (Huan, 2026-08-25: password, sign-out and the walk's
 * doors belong in one place). Account keeps rendering it unchanged. */
export function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordDoor = usePasswordDoor();

  async function submit() {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
    } catch (err) {
      // The server's own sentence where it gave one; otherwise a named
      // outcome that says what is still true — never "change failed".
      setError(
        err instanceof ApiError
          ? err.message
          : 'That did not reach the server, and your password is unchanged.'
      );
    } finally {
      setBusy(false);
    }
  }

  if (!passwordDoor) return null;

  return (
    <div class="account-password">
      <input
        class="account-password__input"
        type="password"
        placeholder="Current password"
        autocomplete="current-password"
        value={current}
        disabled={busy}
        onInput={(e) => setCurrent((e.target as HTMLInputElement).value)}
      />
      <input
        class="account-password__input"
        type="password"
        placeholder="New password"
        autocomplete="new-password"
        value={next}
        disabled={busy}
        onInput={(e) => setNext((e.target as HTMLInputElement).value)}
      />
      <button
        type="button"
        class="account__primary"
        disabled={busy || current === '' || next === ''}
        onClick={() => void submit()}
      >
        Change password
      </button>
      {done && <span class="account__good">Changed. Other devices are signed out.</span>}
      {error && <span class="account__error">{error}</span>}
    </div>
  );
}

// B16 follow-up (Huan, 2026-08-24: "add the passkey registration step
// first before I start"): the enrolment affordance for an ALREADY signed-in
// user. Onboarding has carried this ceremony since C6a for brand-new
// tenants, but an existing user — the cockpit's one real user included —
// had no door to it. Same routes (/auth/passkey/register/options|verify),
// same @simplewebauthn ceremony, same never-blocks posture: a declined or
// unsupported ceremony is a stated outcome, not an error state that traps.
function PasskeyEnrol() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function enrol() {
    setBusy(true);
    setNote(null);
    try {
      const { ceremonyId, options } = await passkeyRegisterOptions();
      const response = await startRegistration({
        optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await passkeyRegisterVerify(ceremonyId, response);
      setDone(true);
    } catch (err) {
      // A cancelled sheet and an unsupported browser both land here — the
      // honest line, and the button stays for another try.
      setNote(err instanceof ApiError ? err.message : 'Not added — the passkey prompt was closed or unsupported here.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return <span class="account__good">Added. Next sign-in offers Face / Touch ID on this device.</span>;
  }
  return (
    <div class="account-passkey">
      <button type="button" class="account__primary" disabled={busy} onClick={() => void enrol()}>
        {busy ? 'Waiting for the prompt…' : 'Add a passkey'}
      </button>
      {note && <span class="account__error">{note}</span>}
    </div>
  );
}

// B17d — the in-shell workspace switcher (the registrants bridge's second
// half). The login-time resolver has always handled >1 membership; this is
// the same choice made available WITHOUT signing out. The list is the
// server's answer (GET /auth/tenants), the current workspace is marked
// with a word, and tapping another hands the whole membership up to App —
// the one writer of the stored id + vertical. With a single membership the
// section still renders: where you are is a fact worth a line, and the
// section teaches by existing what appears when a second workspace does.
export function WorkspaceSwitcher({
  activeTenantId,
  onSwitch,
}: {
  activeTenantId: string | null;
  onSwitch: (tenant: TenantMembership) => void;
}) {
  const [tenants, setTenants] = useState<TenantMembership[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // A failed READ is retryable — nothing was refused and nothing was
  // consumed, the list simply did not arrive. This is the one shape that
  // earns a retry; a 403 or a spent invite never does.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listTenants()
      .then((rows) => { if (!cancelled) { setTenants(rows); setFailed(null); } })
      .catch((err: unknown) => {
        if (!cancelled) setFailed(err instanceof ApiError ? err.message : '');
      });
    return () => { cancelled = true; };
  }, [attempt]);

  if (failed !== null) {
    return (
      <div class="account-workspaces">
        <span class="account__error">
          Your workspaces could not be read.{failed ? ` ${failed}` : ''}
        </span>
        <button
          type="button"
          class="account__primary"
          onClick={() => {
            setFailed(null);
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (tenants === null) {
    return <div class="account-workspaces__skeleton" aria-hidden="true" />;
  }

  return (
    <div class="account-workspaces">
      {tenants.map((t) => {
        const current = t.tenantId === activeTenantId;
        return current ? (
          <div class="account-workspaces__row is-current" key={t.tenantId}>
            <span class="account-workspaces__name">{t.name}</span>
            <span class="account-workspaces__mark">current</span>
          </div>
        ) : (
          <button
            type="button"
            class="account-workspaces__row"
            key={t.tenantId}
            onClick={() => onSwitch(t)}
          >
            <span class="account-workspaces__name">{t.name}</span>
            <span class="account-workspaces__role">{t.role}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Account({ user, activeTenantId, onSwitchTenant, onBack }: AccountProps) {
  const passwordDoor = usePasswordDoor();
  return (
    <div class="account">
      <BackLink label="Today" onClick={onBack} />

      <section class="account__section">
        <h2 class="account__label">Signed in as</h2>
        <p class="account__identity">
          {displayName(user)}
          <span class="account__email">{user.email}</span>
        </p>
      </section>

      <section class="account__section">
        <h2 class="account__label">Workspaces</h2>
        <WorkspaceSwitcher activeTenantId={activeTenantId} onSwitch={onSwitchTenant} />
      </section>

      <section class="account__section">
        <h2 class="account__label">Passkey</h2>
        <PasskeyEnrol />
      </section>

      {passwordDoor ? (
        <section class="account__section">
          <h2 class="account__label">Password</h2>
          <PasswordForm />
        </section>
      ) : null}

      <section class="account__section">
        <h2 class="account__label">Security</h2>
        <SignOutButton />
      </section>
    </div>
  );
}
